import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { CustomButton, CustomModal } from "@ippon/shared-components";
import type { AppDispatch } from "@/store";
import {
  extractExpenseOcr,
  type ExpenseOcrExtracted,
} from "@/store/expenseSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import ExpenseDetailPage from "@/pages/Expenses/Detail";
import ExpenseOcrUploadStep from "./ExpenseOcrUploadStep";
import ExpenseOcrProcessingStep from "./ExpenseOcrProcessingStep";
import ExpenseOcrPdfPanel from "./ExpenseOcrPdfPanel";

interface Props {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  /** 紐付ける budget_category_id (予実→支出の自動リンク) */
  presetBudgetCategoryId?: string;
  /**
   * 予実 CAPEX/OPEX 行から開いた場合に自動セットする expense_type。
   * 予実テーブルの行グループから決まる: expense_capex → "CAPEX" / expense_opex → "OPEX"
   */
  presetExpenseType?: "OPEX" | "CAPEX";
  /** 新規時のデフォルトステータス。予実表からの登録では支払済を初期値にする。 */
  presetStatus?: string;
  /** 登録成功時のコールバック (親の再取得等に使用) */
  onCreated?: (expenseId: string) => void;
}

type Step = "upload" | "processing" | "form";

/**
 * 支出 新規登録モーダル (OCR 対応)。
 *
 * フロー:
 *   1. upload  : 請求書 / 領収書 をドラッグ & ドロップ or クリック選択
 *   2. processing : Azure DI + OpenAI で抽出中
 *   3. form    : ExpenseDetailPage を OCR 結果プリセット付きで描画
 *
 * 「手動で入力する」を押すと OCR をスキップして空の form へ直行。
 */
const ExpenseCreateDrawer: React.FC<Props> = ({
  open,
  onClose,
  propertyId,
  presetBudgetCategoryId,
  presetExpenseType,
  presetStatus,
  onCreated,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();

  const [step, setStep] = useState<Step>("upload");
  const [processingFileName, setProcessingFileName] = useState<string>("");
  const [ocrResult, setOcrResult] = useState<{
    extracted: ExpenseOcrExtracted;
    aiFilledFields: string[];
    warnings: string[];
    document: {
      document_id: string;
      s3_key: string;
      signed_url: string;
      file_name: string;
      file_type: string;
    };
  } | null>(null);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  // 埋め込んだ ExpenseDetailPage(手動入力フォーム) の未保存状態。
  const [embeddedDirty, setEmbeddedDirty] = useState(false);

  // 閉じた直後 / 次の open に備えて常に upload state にリセット
  // (前回の OCR 結果が残ったまま再表示されるのを防ぐ)
  useEffect(() => {
    if (!open) {
      setStep("upload");
      setProcessingFileName("");
      setOcrResult(null);
      setConfirmCloseOpen(false);
      setEmbeddedDirty(false);
    }
  }, [open]);

  const doClose = () => {
    setStep("upload");
    setProcessingFileName("");
    setOcrResult(null);
    setConfirmCloseOpen(false);
    setEmbeddedDirty(false);
    onClose();
  };

  // X / 背景クリック経由のクローズ要求。
  // OCR 処理中・OCR 結果確認中・手動入力で未保存がある時はそのまま閉じず確認モーダルへ。
  const handleRequestClose = () => {
    if (
      step === "processing" ||
      (step === "form" && (!!ocrResult || embeddedDirty))
    ) {
      setConfirmCloseOpen(true);
      return;
    }
    doClose();
  };

  const handleFileSelected = async (file: File) => {
    setProcessingFileName(file.name);
    setStep("processing");
    try {
      const action = await dispatch(
        extractExpenseOcr({ property_id: propertyId, file }),
      );
      if (extractExpenseOcr.fulfilled.match(action)) {
        const ext = action.payload.extracted;
        const aiFilled: string[] = [];
        if (ext.name) aiFilled.push("name");
        if (ext.budget_category_id) aiFilled.push("category");
        if (ext.expense_type) aiFilled.push("expense_type");
        if (ext.amount !== null) aiFilled.push("amount");
        if (ext.payee) aiFilled.push("payee");
        if (ext.tax_rate !== null) aiFilled.push("tax_rate");
        if (ext.tax_amount !== null) aiFilled.push("tax_amount");
        if (ext.registration_number) aiFilled.push("registration_number");
        setOcrResult({
          extracted: ext,
          aiFilledFields: aiFilled,
          warnings: action.payload.warnings || [],
          document: action.payload.document,
        });
        setStep("form");
      } else {
        showToast({
          type: "error",
          message:
            (action.payload as string) ||
            "OCR 抽出に失敗しました。手動入力に切り替えます",
        });
        // 失敗時は手動入力へフォールバック
        setStep("form");
      }
    } catch {
      showToast({
        type: "error",
        message: "OCR 抽出に失敗しました。手動入力に切り替えます",
      });
      setStep("form");
    }
  };

  // OCR が成功して書類が取り込めた form step のみ split レイアウトを有効化。
  // 手動入力フロー (ocrResult 無し) や upload/processing step は従来の 1 カラム。
  const isOcrFormStep = step === "form" && !!ocrResult?.document;

  return (
    <>
    <CustomModal
      openModal={open}
      handleCloseModal={handleRequestClose}
      title={step === "form" ? "支出登録" : "支出スマート抽出"}
      modalType="registerModal"
      addTitleBorder
      dynamicHeight={step !== "form"}
      wide={isOcrFormStep}
      sx={
        isOcrFormStep
          ? undefined
          : {
              maxWidth: step === "form" ? "800px !important" : "600px !important",
            }
      }
      leftPane={
        isOcrFormStep && ocrResult?.document ? (
          <ExpenseOcrPdfPanel
            signedUrl={ocrResult.document.signed_url}
            fileName={ocrResult.document.file_name}
            fileType={ocrResult.document.file_type}
          />
        ) : undefined
      }
    >
      {open && step === "upload" && (
        <ExpenseOcrUploadStep
          onFileSelected={handleFileSelected}
          onSkipToManual={() => setStep("form")}
        />
      )}

      {open && step === "processing" && (
        <ExpenseOcrProcessingStep fileName={processingFileName} />
      )}

      {open && step === "form" && (
        <ExpenseDetailPage
          mode="new"
          overridePropertyId={propertyId}
          presetBudgetCategoryId={
            // AI フローの時は AI の解決結果のみを使う (null ならタブにフォールバックせず空)。
            // 手動フロー (ocrResult が無い) ではタブで選んでいたカテゴリを初期値にする。
            ocrResult
              ? ocrResult.extracted.budget_category_id ?? undefined
              : presetBudgetCategoryId
          }
          presetExpenseType={
            ocrResult?.extracted.expense_type || presetExpenseType
          }
          presetStatus={ocrResult ? undefined : presetStatus}
          presetOcrValues={
            ocrResult
              ? {
                  name: ocrResult.extracted.name ?? undefined,
                  amount:
                    ocrResult.extracted.amount !== null
                      ? String(ocrResult.extracted.amount)
                      : undefined,
                  payee: ocrResult.extracted.payee ?? undefined,
                  partner_name: ocrResult.extracted.partner_name ?? undefined,
                  tax_rate:
                    ocrResult.extracted.tax_rate !== null
                      ? String(ocrResult.extracted.tax_rate)
                      : undefined,
                  tax_amount:
                    ocrResult.extracted.tax_amount !== null
                      ? String(ocrResult.extracted.tax_amount)
                      : undefined,
                  registration_number:
                    ocrResult.extracted.registration_number ?? undefined,
                  expense_type: ocrResult.extracted.expense_type ?? undefined,
                  notes: ocrResult.extracted.notes ?? undefined,
                }
              : undefined
          }
          presetAiFilledFields={ocrResult?.aiFilledFields}
          presetOcrWarnings={ocrResult?.warnings}
          presetBreakdown={ocrResult?.extracted.breakdown}
          presetOcrDocument={ocrResult?.document}
          onCreated={(id) => {
            onCreated?.(id);
            doClose();
          }}
          onCancel={handleRequestClose}
          onDirtyChange={setEmbeddedDirty}
          embedded
        />
      )}
    </CustomModal>

    {/* OCR 処理中 / 結果確認中に閉じようとした時の確認 */}
    <CustomModal
      title={
        step === "form"
          ? "ページ移動の確認"
          : "読み取りを中断しますか？"
      }
      openModal={confirmCloseOpen}
      handleCloseModal={() => setConfirmCloseOpen(false)}
      bodyText=""
      hideCloseButton
      modalType="deleteModal"
    >
      <Box>
        <Typography sx={{ fontSize: "14px", color: "#666" }}>
          {step === "form" ? (
            <>入力中の情報が失われますが、よろしいでしょうか？</>
          ) : (
            <>
              AI による読み取りが完了する前に閉じると、
              <br />
              抽出結果は破棄されます。
            </>
          )}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
        <CustomButton
          label={step === "form" ? "戻る" : "続ける"}
          buttonCategory="cancelModal"
          onClick={() => setConfirmCloseOpen(false)}
        />
        <CustomButton
          label={step === "form" ? "移動" : "中断して閉じる"}
          buttonCategory="deleteModal"
          onClick={doClose}
        />
      </Box>
    </CustomModal>
    </>
  );
};

export default ExpenseCreateDrawer;
